import type { GridColumnDefinition, IGridColumnSort } from '.';
import type GridDataSource from './GridDataSource';
import type { IGridRowCssRule } from './IGridRowCssRule';

interface IGridSetup<TRowData, TDetailRowData = any> {
    /**
     * The column definition for the grid
     */
    readonly GridColumnDefinition: GridColumnDefinition<TRowData>;

    /**
     * The datasource for the grid
     */
    datasource: GridDataSource<TRowData>;

    /**
     * Whether the grid should allow single or multiple row selection
     */
    rowSelection?: 'single' | 'multiple';

    /**
     * Whether the rows should be animated when they are updated or not
     */
    animateRows?: boolean;

    /**
     * This function should return the Id of the row
     * @param data The row data
     * @returns The Id of the row
     */
    rowIdGetter?: (data: TRowData) => string;

    /**
     * Set the css class of the row based on the value of the row
     * Note: The grid wont reapply the css class untill the row is redrawn
     */
    rowClassRules?: IGridRowCssRule<TRowData>[];

    /**
     * This is used as a WatchSource for the rendered rows.
     *
     * If the WatchSource changes, the row will be refreshed using the gridApi.redrawRows calls.'
     *
     * This will remove the row from the dom and redraw it.
     *
     * This is rather heavyhanded, but its the only way I can find to make the grid re-evaluate the rowClassRules
     * @param row The row we are evaluating to see if it should be refreshed
     * @returns Something that changes when the row should be refreshed
     */
    rowRefreshTrigger?: (row?: TRowData) => any;

    /**
     * A unique identifier for the grid. This will enable the grid to remember the column sizes and order and state
     */
    gridkey?: string;

    /**
     * The size of the block cache, This is the number of rows/nodes that will be rendered
     * @default 100
     */
    blockCacheSize?: number;

    /**
     * Hides the statusbar of the grid
     * @default false
     */
    hideStatusbar?: boolean;

    /**
     * The setup for the detail grid
     * TODO: This should have its own type
     */
    detailGridSetup?: GridSetup<TDetailRowData>;

    /**
     * Wether the grid should have a column with selection checkboxes or not
     */
    hasCheckboxSelectionColumn?: boolean;
}
export default class GridSetup<TRowData, TDetailRowData = any> implements IGridSetup<TRowData, TDetailRowData> {
    public GridColumnDefinition: GridColumnDefinition<TRowData>;
    public rowSelection?: 'single' | 'multiple';
    public animateRows?;
    public datasource: GridDataSource<TRowData>;
    public rowIdGetter?: (data: TRowData) => string;
    public rowClassRules?: IGridRowCssRule<TRowData>[];
    public rowRefreshTrigger?: (row?: TRowData) => any;
    public gridkey?: string;
    public blockCacheSize: number;
    public hideStatusbar?: boolean;
    public detailGridSetup?: GridSetup<TDetailRowData>;

    /**
     * Wether the grid should have checkboxes or not on each row
     */
    public hasCheckboxSelectionColumn: boolean;

    public constructor(gridSetup: IGridSetup<TRowData>) {
        this.GridColumnDefinition = gridSetup.GridColumnDefinition;
        this.rowSelection = gridSetup.rowSelection ?? 'single';
        this.animateRows = gridSetup.animateRows ?? true;
        this.datasource = gridSetup.datasource;
        this.rowIdGetter = gridSetup.rowIdGetter;
        this.rowClassRules = gridSetup.rowClassRules;
        this.rowRefreshTrigger = gridSetup.rowRefreshTrigger;
        this.gridkey = gridSetup.gridkey;
        this.blockCacheSize = gridSetup.blockCacheSize ?? 100;
        this.hideStatusbar = gridSetup.hideStatusbar ?? false;
        this.detailGridSetup = gridSetup.detailGridSetup;
        this.hasCheckboxSelectionColumn = gridSetup.hasCheckboxSelectionColumn ?? false;
    }
}
